<template>
  <div>
    <!-- Mobile overlay -->
    <div
      v-if="isSidebarOpen && isMobileView"
      class="fixed inset-0 bg-theme-text bg-opacity-50 z-[90] lg:hidden"
      @click="closeSidebar"
    ></div>

    <!-- Sidebar shell — rail (always visible) + contextual sidebar (per cauldron).

         When the user has the sidebar collapsed, hovering anywhere on
         the shell hover-expands it to its full width (rail + contextual)
         and OVERLAYS the page content. Leaving the shell snaps it back
         to the rail. The persisted `isSidebarCollapsed` store value is
         unchanged during hover, so the page-content's left margin (set
         in App.vue / PageHeader.vue / _app.css) stays at the collapsed
         offset and the expansion doesn't reflow anything beneath. -->
    <aside
      :class="[
        'fixed left-0 top-0 h-screen z-[100] transition-[transform,width,box-shadow] duration-200 ease-in-out flex flex-col',
        'bg-theme-surface dark:bg-theme-bg border-r border-theme-border dark:border-theme-border-strong',
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        effectiveCollapsed && !isMobileView ? 'w-16' : 'w-72',
        isHoverExpanded ? 'shadow-theme-elevated' : '',
      ]"
      @mouseenter="onShellEnter"
      @mouseleave="onShellLeave"
    >
      <!-- Brand header — logo + (un)collapse toggle. Uses `effectiveCollapsed`
           so during hover-expand we show the full PROFITEIA brand and the
           collapse button (which, when clicked, pins the persistent
           uncollapsed state). -->
      <div
        :class="[
          'sidebar-header',
          effectiveCollapsed ? 'sidebar-header--collapsed' : 'sidebar-header--expanded',
        ]"
      >
        <div
          class="sidebar-header__brand-row"
          :class="{ 'sidebar-header__brand-row--expanded': !effectiveCollapsed }"
        >
          <transition name="fade" mode="out-in">
            <span
              v-if="!effectiveCollapsed"
              class="sidebar-logo bg-clip-text text-transparent bg-gradient-to-r from-theme-palette-emerald to-theme-palette-teal font-azonix text-lg tracking-wide drop-shadow-theme-brand"
            >
              PROFITEIA
            </span>
            <span
              v-else
              class="sidebar-logo bg-clip-text text-transparent bg-gradient-to-r from-theme-palette-emerald to-theme-palette-teal font-azonix text-lg drop-shadow-theme-brand"
            >
              P
            </span>
          </transition>

          <!-- During hover-expand we show a "pin" affordance instead of a
               plain collapse arrow — clicking it pins the expansion as
               the persistent state so the sidebar stops snapping shut. -->
          <button
            v-if="!effectiveCollapsed && !isMobileView"
            type="button"
            class="sidebar-brand-toggle hidden lg:inline-flex"
            :aria-label="isHoverExpanded ? t('sidebar.aria.expand') : t('sidebar.aria.collapse')"
            :title="isHoverExpanded ? t('sidebar.aria.expand') : t('sidebar.aria.collapse')"
            @click="toggleCollapse"
          >
            <icon :path="isHoverExpanded ? mdiPinOutline : mdiChevronLeft" size="15" />
          </button>
        </div>

        <button
          @click="closeSidebar"
          type="button"
          class="sidebar-chrome-toggle sidebar-chrome-toggle--mobile lg:hidden flex absolute right-4"
          :aria-label="t('sidebar.aria.close')"
          :title="t('sidebar.aria.close')"
        >
          <icon :path="mdiClose" size="16" />
        </button>
      </div>


      <!-- Shell body — two layouts driven by `effectiveCollapsed`
           (= persistent collapsed state, overridden to `false` while the
           shell is being hover-expanded):
             1. **Expanded + no cauldron** → flat menu (Home + all
                cauldron entry points).
             2. **Expanded + in a cauldron** → rail + contextual sidebar.
             3. **Collapsed (no hover)** → rail only.

           Hovering anywhere on the shell while collapsed flips
           `effectiveCollapsed` to false, so layout 3 promotes to
           layout 1 or 2 depending on whether the user is in a cauldron.
           The page-content margin in App.vue stays at the persistent
           collapsed offset (`lg:ml-16`), so the expanded shell visually
           overlays the page rather than reflowing it. -->
      <div class="shell-body relative flex-1 min-h-0">
        <!-- API-only tenants get the API portal menu instead of the rail,
             flat menu and cauldrons (no Home, News or tools). -->
        <div v-if="isApiOnly" class="mode-pane">
          <api-sidebar
            class="flex-1 min-w-0"
            :collapsed="effectiveCollapsed"
            @navigate="handleMenuClick"
          />
        </div>
        <transition v-else :name="modeSwapName">
          <!-- Flat-menu mode — no active cauldron AND room for labels
               (mobile always has full width; desktop when not effectively
               collapsed). -->
          <div
            v-if="showFlatMode"
            key="flat"
            class="mode-pane"
          >
            <flat-menu-sidebar class="flex-1 min-w-0" @navigate="handleMenuClick" />
          </div>

          <!-- Rail + contextual-sidebar mode. The rail always renders;
               the contextual pane fades in when the shell is expanded
               (persistently OR by hover) and there's a cauldron to
               surface. -->
          <div v-else key="cauldron" class="mode-pane flex">
            <cauldron-rail
              :bordered="showRailBorder"
              @hover-cauldron="onPreviewCauldron"
            />

            <transition name="contextual-show">
              <div
                v-if="(!effectiveCollapsed || isMobileView) && displayedCauldronId"
                class="flex-1 min-w-0 flex flex-col contextual-host"
              >
                <transition name="cauldron-swap" mode="out-in">
                  <component
                    v-if="displayedSidebarComponent"
                    :is="displayedSidebarComponent"
                    :key="displayedCauldronId"
                    :collapsed="false"
                    @navigate="handleMenuClick"
                  />
                </transition>
              </div>
            </transition>
          </div>
        </transition>
      </div>
    </aside>
  </div>
</template>

<script setup>
  /* eslint-disable no-undef */
  import Icon from '@/components/Icon';
  import CauldronRail from '@/components/navigation/CauldronRail.vue';
  import FlatMenuSidebar from '@/components/navigation/FlatMenuSidebar.vue';
  import ApiSidebar from '@/features/api-store/components/ApiSidebar.vue';
  import ForwardSidebar from '@/components/navigation/cauldrons/ForwardSidebar.vue';
  import FlexSidebar from '@/components/navigation/cauldrons/FlexSidebar.vue';
  import ExecutiveSidebar from '@/components/navigation/cauldrons/ExecutiveSidebar.vue';
  import FavouritesSidebar from '@/components/navigation/cauldrons/FavouritesSidebar.vue';
  import PersonalisationSidebar from '@/components/navigation/cauldrons/PersonalisationSidebar.vue';
  import PlatformSidebar from '@/components/navigation/cauldrons/PlatformSidebar.vue';
  import { useActiveCauldron } from '@/composables/useActiveCauldron';
  import { CAULDRON_IDS } from '@/config/domain/cauldrons';
  import { useDisplayStore } from '@/stores/useDisplayStore';
  import { useEntitlementsStore } from '@/stores/useEntitlementsStore';
  import {
    mdiChevronLeft,
    mdiClose,
    mdiPinOutline,
  } from '@mdi/js';
  import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  const displayStore = useDisplayStore();
  const entitlementsStore = useEntitlementsStore();
  const { t } = useI18n();
  const isApiOnly = computed(() => entitlementsStore.isApiOnly);

  // Sidebar state
  const isMobileView = ref(false);
  const isCollapsed = computed({
    get: () => displayStore.isSidebarCollapsed,
    set: (value) => displayStore.setSidebarCollapsed(value),
  });

  const isSidebarOpen = computed({
    get: () => displayStore.isSidebarOpen,
    set: (value) => displayStore.setSidebarOpen(value),
  });

  // Active cauldron → contextual sidebar swap. When no cauldron applies
  // (Home, login, embedded views) the contextual sidebar renders nothing
  // — the rail is the only sidebar surface in those contexts.
  const { activeCauldronId } = useActiveCauldron();
  const SIDEBAR_BY_CAULDRON = {
    [CAULDRON_IDS.FORWARD]: ForwardSidebar,
    [CAULDRON_IDS.FLEX]: FlexSidebar,
    [CAULDRON_IDS.EXECUTIVE]: ExecutiveSidebar,
    [CAULDRON_IDS.PERSONALISATION]: PersonalisationSidebar,
    [CAULDRON_IDS.FAVOURITES]: FavouritesSidebar,
    [CAULDRON_IDS.PLATFORM]: PlatformSidebar,
  };
  const activeSidebarComponent = computed(
    () => SIDEBAR_BY_CAULDRON[activeCauldronId.value] || null,
  );
  // `isInCauldron` drives the shell's outer state machine: when false,
  // the sidebar renders the FlatMenuSidebar; when true, it renders the
  // rail + contextual sidebar (or rail alone when collapsed).
  const isInCauldron = computed(() => activeCauldronId.value !== null);

  // ── Hover preview ────────────────────────────────────────────────────
  //
  // Hovering a rail icon swaps the contextual sidebar to THAT cauldron's
  // content WITHOUT navigating. This makes the rail feel fluid — you can
  // peek into Tools / Personalisation / Platform while staying on your
  // current page, and only commit by clicking. The preview is cleared
  // when the user leaves the whole shell (`onShellLeave`); brief hover
  // gaps between rail buttons keep the previous preview so quick
  // sweeps don't flicker.
  const previewCauldronId = ref(null);
  let previewClearTimer = null;

  function onPreviewCauldron(id) {
    // `id` is `null` for Home / no-cauldron buttons. Treat null as
    // "clear preview" so the contextual reverts to whatever's active.
    previewCauldronId.value = id || null;
  }

  // The cauldron the contextual sidebar is showing right now: the
  // hovered/previewed one when set, otherwise whichever cauldron the
  // user is currently inside.
  const displayedCauldronId = computed(
    () => previewCauldronId.value || activeCauldronId.value || null,
  );
  const displayedSidebarComponent = computed(
    () => SIDEBAR_BY_CAULDRON[displayedCauldronId.value] || null,
  );

  // ── Hover-to-expand ──────────────────────────────────────────────────
  //
  // When the sidebar is persistently collapsed and the user hovers
  // anywhere on the shell, we promote the visual state to expanded
  // (rail + contextual sidebar) and OVERLAY the page content. Leaving
  // the shell snaps it back. The persisted store value is untouched, so
  // the page-content margin (set in App.vue / PageHeader.vue) stays at
  // the collapsed offset and the overlay doesn't reflow anything.
  //
  // Short delays smooth the interaction: a small enter-delay prevents
  // the sidebar from popping open during incidental mouse crossings
  // (e.g. dragging a window past it); a longer leave-delay lets the
  // cursor cross the rail/contextual seam without triggering a snap.
  //
  // Declared BEFORE `showFlatMode` / `modeSwapName` below, because those
  // computeds reference `effectiveCollapsed` — Vue's `setup()` runs the
  // function body top-to-bottom and TDZ rules apply, so the order matters.
  const isHoverExpanded = ref(false);
  let hoverEnterTimer = null;
  let hoverLeaveTimer = null;
  const HOVER_ENTER_DELAY_MS = 80;
  const HOVER_LEAVE_DELAY_MS = 200;

  function onShellEnter() {
    // Mobile uses the drawer pattern (`isSidebarOpen`), not hover-expand;
    // skip the whole flow. Same when the sidebar isn't actually collapsed.
    if (isMobileView.value || !isCollapsed.value) return;
    if (hoverLeaveTimer) {
      clearTimeout(hoverLeaveTimer);
      hoverLeaveTimer = null;
    }
    if (hoverEnterTimer) return;
    hoverEnterTimer = setTimeout(() => {
      isHoverExpanded.value = true;
      hoverEnterTimer = null;
    }, HOVER_ENTER_DELAY_MS);
  }
  function onShellLeave() {
    if (hoverEnterTimer) {
      clearTimeout(hoverEnterTimer);
      hoverEnterTimer = null;
    }
    // Clear any active cauldron preview when the cursor leaves the
    // shell entirely — same gesture that collapses an expanded shell.
    // Brief hover-throughs between rail buttons stay in preview because
    // the cursor never actually exits the shell.
    previewCauldronId.value = null;
    if (!isHoverExpanded.value) return;
    if (hoverLeaveTimer) clearTimeout(hoverLeaveTimer);
    hoverLeaveTimer = setTimeout(() => {
      isHoverExpanded.value = false;
      hoverLeaveTimer = null;
    }, HOVER_LEAVE_DELAY_MS);
  }

  // The "visual" collapsed state. Everything inside the sidebar that
  // toggles based on collapsed/expanded chrome should read this rather
  // than the raw store value, so hover-expand drives the visuals
  // correctly while the persisted preference stays put.
  const effectiveCollapsed = computed(() => {
    if (isMobileView.value) return false;
    return isCollapsed.value && !isHoverExpanded.value;
  });

  // Rail divider is only useful when a contextual pane sits next to it.
  // In rail-only mode the outer shell's border is already the visual
  // right edge, so an extra rail border would render as a double line.
  const showRailBorder = computed(
    () => !effectiveCollapsed.value && isInCauldron.value && !isMobileView.value,
  );

  // The body has exactly two layouts: the flat menu, and the
  // rail+contextual pair. Flat mode is only shown when there's room for
  // labels (mobile always, desktop only when not effectively collapsed —
  // i.e. either the user has pinned it open OR is currently hovering);
  // otherwise we fall through to the rail-based layout (rail alone if
  // there's no cauldron, rail+contextual if there is).
  const showFlatMode = computed(
    () => !isInCauldron.value && (isMobileView.value || !effectiveCollapsed.value),
  );

  // Direction-aware transition name. When the user enters a cauldron from
  // flat mode we slide the new pane in from the right; when they leave a
  // cauldron back to flat we slide it back from the left. The result is a
  // continuous "drill in / drill out" feel rather than a directionless
  // crossfade. We watch `showFlatMode` instead of `isInCauldron` so the
  // collapse toggle (which swaps modes without changing cauldron) also
  // gets a sensible direction.
  const modeSwapName = ref('mode-swap-forward');
  watch(showFlatMode, (next, prev) => {
    if (next === prev) return;
    // Going FROM flat (true → false) → entering cauldron → forward.
    // Going TO flat (false → true) → returning to home → backward.
    modeSwapName.value = next ? 'mode-swap-backward' : 'mode-swap-forward';
  });

  // Functions
  function toggleCollapse() {
    if (isMobileView.value) return;
    displayStore.setSidebarCollapsed(!isCollapsed.value);
  }

  function closeSidebar() {
    if (isMobileView.value) {
      isSidebarOpen.value = false;
    }
  }

  function handleMenuClick() {
    // Once the user actually navigates, the preview goal is fulfilled —
    // drop it so the contextual reflects the new active cauldron. When
    // the click comes from a previewed cauldron, defer the reset until the
    // router-link has processed the same click event; clearing immediately
    // unmounts the previewed sidebar and can cancel the navigation.
    if (previewClearTimer) clearTimeout(previewClearTimer);
    previewClearTimer = setTimeout(() => {
      previewCauldronId.value = null;
      previewClearTimer = null;
    }, 0);
    closeSidebar();
  }

  function checkMobileView() {
    const wasMobile = isMobileView.value;
    isMobileView.value = window.innerWidth < 1024;
    if (isMobileView.value) {
      isSidebarOpen.value = false;
      if (isCollapsed.value) {
        displayStore.setSidebarCollapsed(false);
      }
    } else if (wasMobile && !isMobileView.value) {
      isSidebarOpen.value = true;
    }
  }

  // Lifecycle
  let resizeObserver = null;
  onMounted(() => {
    checkMobileView();
    resizeObserver = new ResizeObserver(() => checkMobileView());
    resizeObserver.observe(document.body);
    window.addEventListener('resize', checkMobileView);
  });
  onUnmounted(() => {
    if (previewClearTimer) clearTimeout(previewClearTimer);
    if (resizeObserver) resizeObserver.disconnect();
    window.removeEventListener('resize', checkMobileView);
  });
</script>

<style scoped>
  .sidebar-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  /* Header dimensions are IDENTICAL across collapsed/expanded states so
     the rail icons beneath don't shift vertically when the sidebar
     hover-expands. The horizontal padding differs (the expanded header
     hugs the wider PROFITEIA word-mark; the collapsed one hugs the single
     "P" glyph), but the total block height is the same `4rem` (64px) in
     both states. */
  .sidebar-header--expanded {
    padding: 0.875rem 1rem;
    min-height: 4rem;
    height: 4rem;
  }

  .sidebar-header--collapsed {
    padding: 0.875rem 0.5rem;
    min-height: 4rem;
    height: 4rem;
  }

  .sidebar-header__brand-row {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    min-width: 0;
    line-height: 1;
  }

  .sidebar-header__brand-row--expanded {
    width: 100%;
    justify-content: space-between;
  }

  .sidebar-logo {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  .sidebar-brand-toggle {
    align-items: center;
    justify-content: center;
    margin-left: auto;
    width: 1.75rem;
    height: 1.75rem;
    border: none;
    border-radius: 999px;
    background: transparent;
    color: rgb(var(--color-text-muted));
    transition: background-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
  }

  .sidebar-brand-toggle:hover {
    background: rgb(var(--color-bg-app) / 0.9);
    color: rgb(var(--color-text-secondary));
  }

  .sidebar-brand-toggle:focus,
  .sidebar-brand-toggle:focus-visible {
    outline: none;
    background: rgb(var(--color-brand-primary-soft) / 0.9);
    color: rgb(var(--color-brand-primary));
    box-shadow: 0 0 0 3px rgb(var(--color-brand-primary) / 0.1);
  }

  .sidebar-chrome-toggle {
    align-items: center;
    justify-content: center;
    border: 1px solid rgb(var(--color-border-default));
    border-radius: 0.875rem;
    background: rgb(var(--color-static-white));
    color: rgb(var(--color-border-subtle));
    transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease,
      box-shadow 0.15s ease, transform 0.15s ease;
    box-shadow: 0 1px 2px rgb(var(--color-text-primary) / 0.04);
  }

  .sidebar-chrome-toggle:hover {
    border-color: rgb(var(--color-text-muted));
    background: rgb(var(--color-bg-app));
    color: rgb(var(--color-text-primary));
  }

  .sidebar-chrome-toggle:focus,
  .sidebar-chrome-toggle:focus-visible {
    outline: none;
    border-color: rgb(var(--color-brand-primary));
    box-shadow: 0 0 0 3px rgb(var(--color-brand-primary) / 0.14);
  }

  .sidebar-chrome-toggle--desktop,
  .sidebar-chrome-toggle--mobile {
    width: 2.75rem;
    height: 2.75rem;
  }

  /* Contextual pane reveal — fades in alongside the shell's width
     transition when the sidebar hover-expands (or pins open). Slightly
     longer duration than the width animation so the contextual content
     finishes settling AFTER the shell stops moving — feels more polished
     than the content arriving simultaneously with the width change. */
  .contextual-show-enter-active,
  .contextual-show-leave-active {
    transition: opacity 240ms ease, transform 280ms cubic-bezier(0.32, 0.72, 0, 1);
    will-change: opacity, transform;
  }
  .contextual-show-enter-from,
  .contextual-show-leave-to {
    opacity: 0;
    transform: translateX(-10px);
  }

  /* Shell-body panes are absolutely positioned during the transition so
     the outgoing and incoming layouts can overlap (cross-fade), rather
     than waiting for one to finish before the other starts. The pane
     itself is a normal flex container — the absolute positioning just
     lifts it out of layout while the swap is in flight. */
  .shell-body { overflow: hidden; }
  .mode-pane {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: row;
  }

  /* Forward swap: flat → cauldron. The incoming cauldron pane slides in
     from the right; the outgoing flat menu slides slightly to the left
     and fades out. */
  .mode-swap-forward-enter-active,
  .mode-swap-forward-leave-active {
    transition: opacity 220ms ease, transform 320ms cubic-bezier(0.32, 0.72, 0, 1);
    will-change: opacity, transform;
  }
  .mode-swap-forward-enter-from {
    opacity: 0;
    transform: translateX(14px);
  }
  .mode-swap-forward-leave-to {
    opacity: 0;
    transform: translateX(-10px);
  }

  /* Backward swap: cauldron → flat. Mirror of forward — incoming flat
     menu slides in from the left, cauldron pane drifts to the right. */
  .mode-swap-backward-enter-active,
  .mode-swap-backward-leave-active {
    transition: opacity 220ms ease, transform 320ms cubic-bezier(0.32, 0.72, 0, 1);
    will-change: opacity, transform;
  }
  .mode-swap-backward-enter-from {
    opacity: 0;
    transform: translateX(-14px);
  }
  .mode-swap-backward-leave-to {
    opacity: 0;
    transform: translateX(10px);
  }

  /* Cauldron-to-cauldron swap inside the contextual pane. Faster and
     shorter throw than the mode swap — the user is moving inside the
     same "drilled-in" mode, so the motion is lighter. */
  .cauldron-swap-enter-active,
  .cauldron-swap-leave-active {
    transition: opacity 160ms ease, transform 200ms cubic-bezier(0.32, 0.72, 0, 1);
    will-change: opacity, transform;
  }
  .cauldron-swap-enter-from {
    opacity: 0;
    transform: translateX(8px);
  }
  .cauldron-swap-leave-to {
    opacity: 0;
    transform: translateX(-6px);
  }

  /* Per-row stagger inside any sidebar `<nav>` that's currently being
     transitioned in. The animation only runs on rows inside a freshly-
     mounted pane (descendant of `.mode-pane` or `.contextual-host > *`),
     so it triggers naturally on mode-swap and cauldron-swap but NOT on
     route changes that only re-render a single row. Limited to the first
     12 rows so longer favourites lists don't ripple endlessly. */
  @media (prefers-reduced-motion: no-preference) {
    .mode-pane nav > *,
    .contextual-host > * nav > * {
      animation: sidebar-row-in 380ms cubic-bezier(0.32, 0.72, 0, 1) both;
      animation-delay: calc(var(--sidebar-row-delay, 0ms));
    }
    .mode-pane nav > *:nth-child(1),
    .contextual-host > * nav > *:nth-child(1) { --sidebar-row-delay: 40ms; }
    .mode-pane nav > *:nth-child(2),
    .contextual-host > * nav > *:nth-child(2) { --sidebar-row-delay: 70ms; }
    .mode-pane nav > *:nth-child(3),
    .contextual-host > * nav > *:nth-child(3) { --sidebar-row-delay: 95ms; }
    .mode-pane nav > *:nth-child(4),
    .contextual-host > * nav > *:nth-child(4) { --sidebar-row-delay: 115ms; }
    .mode-pane nav > *:nth-child(5),
    .contextual-host > * nav > *:nth-child(5) { --sidebar-row-delay: 130ms; }
    .mode-pane nav > *:nth-child(6),
    .contextual-host > * nav > *:nth-child(6) { --sidebar-row-delay: 145ms; }
    .mode-pane nav > *:nth-child(7),
    .contextual-host > * nav > *:nth-child(7) { --sidebar-row-delay: 158ms; }
    .mode-pane nav > *:nth-child(8),
    .contextual-host > * nav > *:nth-child(8) { --sidebar-row-delay: 170ms; }
    .mode-pane nav > *:nth-child(9),
    .contextual-host > * nav > *:nth-child(9) { --sidebar-row-delay: 180ms; }
    .mode-pane nav > *:nth-child(10),
    .contextual-host > * nav > *:nth-child(10) { --sidebar-row-delay: 190ms; }
    .mode-pane nav > *:nth-child(11),
    .contextual-host > * nav > *:nth-child(11) { --sidebar-row-delay: 198ms; }
    .mode-pane nav > *:nth-child(12),
    .contextual-host > * nav > *:nth-child(12) { --sidebar-row-delay: 205ms; }
  }

  @keyframes sidebar-row-in {
    0% {
      opacity: 0;
      transform: translateX(6px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  /* The rail itself also fades in cleanly when the cauldron pane mounts.
     This stops the icons from "popping" while the rest of the pane is
     still in-flight. */
  .mode-pane :deep(.cauldron-rail) {
    animation: rail-in 320ms cubic-bezier(0.32, 0.72, 0, 1) both;
  }
  @keyframes rail-in {
    0% {
      opacity: 0;
      transform: translateX(-6px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }

  /* Shared section label used by every contextual sidebar. */
  :global(.sidebar-section-label) {
    margin: 1rem 0 0.35rem;
    padding: 0 0.625rem;
    color: rgb(var(--color-text-muted));
    /* Azonix brand label — short, fixed, all-caps category words sit inside
       the readable Azonix envelope (small + tracked). Neutral muted colour,
       no emerald. Azonix is single-weight, so weight is 400 (700 is a no-op). */
    font-family: var(--font-display);
    font-size: 0.625rem;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.14em;
    line-height: 1rem;
  }

  .dark .sidebar-chrome-toggle {
    border-color: rgb(var(--color-border-default));
    background: rgb(var(--color-static-white) / 0.02);
    color: rgb(var(--color-border-subtle));
    box-shadow: none;
  }

  .dark .sidebar-brand-toggle {
    color: rgb(var(--color-text-muted));
    background: transparent;
  }

  .dark .sidebar-brand-toggle:hover {
    background: rgb(var(--color-border-subtle) / 0.72);
    color: rgb(var(--color-border-subtle));
  }
</style>
